import type { SmsVoiceNumberFilters } from '@reliance/types/filters/SmsVoiceNumberFilters';
import type { SmsVoiceNumberHistoryFilters } from '@reliance/types/filters/SmsVoiceNumberHistoryFilters';
import type { SmsVoiceNumberIssueFilters } from '@reliance/types/filters/SmsVoiceNumberIssueFilters';
import type { SmsVoiceNumber } from '@reliance/types/models/SmsVoiceNumber';
import type { SmsVoiceNumberDetail } from '@reliance/types/models/SmsVoiceNumber';
import type { SmsVoiceNumberHistory } from '@reliance/types/models/SmsVoiceNumberHistory';
import type { SmsVoiceNumberIssue } from '@reliance/types/models/SmsVoiceNumberIssue';
import type { PaginatedResponse } from '@reliance/types/responses/PaginatedResponse';
import { useAsync } from 'react-async-hook';
import api from '../common/api';

export type SmsVoiceNumberListResponse = PaginatedResponse<SmsVoiceNumber> & {
  needingReturn?: number;
};

export const useSmsVoiceNumbers = (filters?: SmsVoiceNumberFilters) => {
  const filterKey = JSON.stringify(filters ?? {});
  return useAsync<SmsVoiceNumberListResponse>(
    () => api.get<SmsVoiceNumberListResponse>('/smsvoice-number', { params: filters }).then((r) => r.data),
    [filterKey]
  );
};

export const useSmsVoiceNumbersByInmateId = (
  inmateId: number | null | undefined,
  filters?: SmsVoiceNumberFilters
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<SmsVoiceNumber>>(
    (currentInmateId?: number | null | undefined) => {
      const id = currentInmateId !== undefined ? currentInmateId : inmateId;
      if (!id) {
        return null;
      }

      const params: SmsVoiceNumberFilters = {
        ...filters,
        inmateId: id,
      };

      return api
        .get<PaginatedResponse<SmsVoiceNumber>>('/smsvoice-number', { params })
        .then((r) => r.data);
    },
    inmateId === null ? [] : [inmateId, filterKey]
  );
};

export const useSmsVoiceNumberById = (id: number | null | undefined) =>
  useAsync<SmsVoiceNumberDetail>(
    (currentId?: number | null | undefined) => {
      const resolvedId = currentId !== undefined ? currentId : id;
      if (resolvedId == null || !Number.isFinite(resolvedId)) {
        return null;
      }
      return api.get<SmsVoiceNumberDetail>(`/smsvoice-number/${resolvedId}`).then((r) => r.data);
    },
    [id]
  );

export const useSmsVoiceNumberByPhoneNumber = (phoneNumber: string | null | undefined) =>
  useAsync<SmsVoiceNumberDetail>(
    (currentPhoneNumber?: string | null | undefined) => {
      const resolvedPhoneNumber =
        currentPhoneNumber !== undefined ? currentPhoneNumber : phoneNumber;
      if (resolvedPhoneNumber == null || resolvedPhoneNumber.trim() === '') {
        return null;
      }
      return api
        .get<SmsVoiceNumberDetail>(
          `/smsvoice-number/by-ani/${encodeURIComponent(resolvedPhoneNumber)}`
        )
        .then((r) => r.data);
    },
    [phoneNumber]
  );

export const useSmsVoiceNumberHistoryById = (
  id: number | null | undefined,
  filters?: SmsVoiceNumberHistoryFilters
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<SmsVoiceNumberHistory>>(
    (currentId?: number | null | undefined, currentFilters?: SmsVoiceNumberHistoryFilters) => {
      const resolvedId = currentId !== undefined ? currentId : id;
      if (resolvedId == null || !Number.isFinite(resolvedId)) {
        return null;
      }
      const params = currentFilters !== undefined ? currentFilters : filters;
      return api
        .get<PaginatedResponse<SmsVoiceNumberHistory>>(`/smsvoice-number/${resolvedId}/history`, {
          params,
        })
        .then((r) => r.data);
    },
    [id, filterKey]
  );
};

export const useSmsVoiceNumberHistoryByPhoneNumber = (
  phoneNumber: string | null | undefined,
  filters?: SmsVoiceNumberHistoryFilters
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<SmsVoiceNumberHistory>>(
    (
      currentPhoneNumber?: string | null | undefined,
      currentFilters?: SmsVoiceNumberHistoryFilters
    ) => {
      const resolvedPhoneNumber =
        currentPhoneNumber !== undefined ? currentPhoneNumber : phoneNumber;
      if (resolvedPhoneNumber == null || resolvedPhoneNumber.trim() === '') {
        return null;
      }
      const params = currentFilters !== undefined ? currentFilters : filters;
      return api
        .get<PaginatedResponse<SmsVoiceNumberHistory>>(
          `/smsvoice-number/by-ani/${encodeURIComponent(resolvedPhoneNumber)}/history`,
          { params }
        )
        .then((r) => r.data);
    },
    [phoneNumber, filterKey]
  );
};

export const useSmsVoiceNumberIssuesById = (
  id: number | null | undefined,
  filters?: Omit<SmsVoiceNumberIssueFilters, 'smsVoiceNumberId'>
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<SmsVoiceNumberIssue>>(
    (currentId?: number | null | undefined, currentFilters?: SmsVoiceNumberIssueFilters) => {
      const resolvedId = currentId !== undefined ? currentId : id;
      if (resolvedId == null || !Number.isFinite(resolvedId)) {
        return null;
      }
      const baseFilters = currentFilters !== undefined ? currentFilters : filters;
      const params: SmsVoiceNumberIssueFilters = {
        ...baseFilters,
        smsVoiceNumberId: resolvedId,
      };
      return api
        .get<PaginatedResponse<SmsVoiceNumberIssue>>('/smsvoice-number/issues', { params })
        .then((r) => r.data);
    },
    [id, filterKey]
  );
};

export const useSmsVoiceNumberIssuesByPhoneNumber = (
  phoneNumber: string | null | undefined,
  filters?: Omit<SmsVoiceNumberIssueFilters, 'phoneNumber' | 'smsVoiceNumberId'>
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<SmsVoiceNumberIssue>>(
    (
      currentPhoneNumber?: string | null | undefined,
      currentFilters?: SmsVoiceNumberIssueFilters
    ) => {
      const resolvedPhoneNumber =
        currentPhoneNumber !== undefined ? currentPhoneNumber : phoneNumber;
      if (resolvedPhoneNumber == null || resolvedPhoneNumber.trim() === '') {
        return null;
      }
      const baseFilters = currentFilters !== undefined ? currentFilters : filters;
      const params: SmsVoiceNumberIssueFilters = {
        ...baseFilters,
        phoneNumber: resolvedPhoneNumber,
      };
      return api
        .get<PaginatedResponse<SmsVoiceNumberIssue>>('/smsvoice-number/issues', { params })
        .then((r) => r.data);
    },
    [phoneNumber, filterKey]
  );
};
